import type {
  AmericanTrailingReturns,
  BrazilianTrailingReturns,
} from "components/issuers/trailingReturns/TrailingReturns";
import TrailingReturns from "components/issuers/trailingReturns/TrailingReturns";
import { Popover } from "lib/miolo";
import parseDateYmd from "lib/utils/parseDateYmd";
import trimEnd from "lodash/trimEnd";
import numeral from "numeral";
import { useState } from "react";
import { Hover } from "react-powerplug";
import type { IBrazilianRealEstateFundIssuer, ITrailingReturns } from "types/graphqlTypes";

interface EarningsResultsEventDetailsProps {
  areFinancialsUpdated: boolean | null;
  issuer:
    | BrazilianTrailingReturns
    | AmericanTrailingReturns
    | Pick<IBrazilianRealEstateFundIssuer, "__typename" | "trailingReturns">;
  trailingReturns: ITrailingReturns[];
}

export function EarningsResultsEventDetails({
  areFinancialsUpdated,
  issuer,
  trailingReturns,
}: EarningsResultsEventDetailsProps) {
  return (
    <div className="flex items-center justify-between gap-2">
      <TrailingReturns issuer={issuer} trailingReturns={trailingReturns} />

      {areFinancialsUpdated && (
        <span className="inline-flex h-7 items-center rounded-md border bg-blue-50 p-1 text-blue-800">
          <CheckCircleIcon className="mr-1.5 h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden="true" />
          Atualizado
        </span>
      )}
    </div>
  );
}

interface IPaymentOfDividendsEventDetailsProps {
  eventDate: string;
  excludingDate: string | null;
  recordDate: string | null;
  amountPaid: number | null;
  issuerCountry: "US" | "BR" | undefined;
}

export function PaymentOfDividendsEventDetails({
  amountPaid,
  eventDate,
  excludingDate,
  recordDate,
  issuerCountry,
}: IPaymentOfDividendsEventDetailsProps) {
  const name = `PaymentOfDividendsEventDetails/${eventDate}`;
  const parsedEventDate = parseDateYmd(eventDate);

  return (
    <EventDetailsPopover name={name}>
      <>
        {recordDate && (
          <>
            <strong>Data-com</strong>
            <time className="font-monospace" dateTime={parseDateYmd(recordDate).toString()}>
              {parseDateYmd(recordDate).toLocaleDateString("pt-BR")}
            </time>
          </>
        )}

        {excludingDate && (
          <>
            <strong>Data-ex</strong>
            <time className="font-monospace" dateTime={parseDateYmd(excludingDate).toString()}>
              {parseDateYmd(excludingDate).toLocaleDateString("pt-BR")}
            </time>
          </>
        )}

        <strong>Pagamento</strong>
        <time className="font-monospace" dateTime={parsedEventDate.toString()}>
          {parsedEventDate.toLocaleDateString("pt-BR")}
        </time>

        <strong>Valor</strong>
        <span className="font-monospace">{`${issuerCountry === "US" ? "$" : "R$"} ${amountPaid?.toFixed(3)}`}</span>
      </>
    </EventDetailsPopover>
  );
}

interface IPaymentOfStockDividendsEventDetailsProps {
  excludingDate: string;
  sharePaid: number;
}

export function PaymentOfStockDividendsEventDetails({
  excludingDate,
  sharePaid,
}: IPaymentOfStockDividendsEventDetailsProps) {
  const name = `PaymentOfStockDividendsEventDetails/${excludingDate}`;

  return (
    <EventDetailsPopover name={name}>
      <>
        <strong>Data de Conversão</strong>
        <time className="font-monospace" dateTime={parseDateYmd(excludingDate).toString()}>
          {parseDateYmd(excludingDate).toLocaleDateString("pt-BR")}
        </time>

        <strong>Ações Bonificadas</strong>
        <span className="font-monospace">{sharePaid?.toFixed(3)}</span>
      </>
    </EventDetailsPopover>
  );
}
interface IStockSplitEventDetailsProps {
  excludingDate: string;
  splitFrom: number | null;
  splitTo: number | null;
}

export function StockSplitEventDetails({ excludingDate, splitFrom, splitTo }: IStockSplitEventDetailsProps) {
  const name = `StockSplitEventDetails/${excludingDate}`;
  const parsedExcludingDate = parseDateYmd(excludingDate);

  return (
    <EventDetailsPopover name={name}>
      <>
        <strong>Data de Conversão</strong>
        <time className="font-monospace" dateTime={parsedExcludingDate.toString()}>
          {parsedExcludingDate.toLocaleDateString("pt-BR")}
        </time>

        {splitFrom && splitTo && (
          <>
            <strong>Proporção</strong>
            <span className="font-monospace">
              {splitFrom}:{splitTo}
            </span>
          </>
        )}
      </>
    </EventDetailsPopover>
  );
}

interface IRightsIssueEventDetailsProps {
  eventDate: string;
  recordDate: string | null;
  newShare: number | null;
  subscriptionPrice: number | null;
  issuerCountry: "US" | "BR" | undefined;
}

export function RightsIssueEventDetails({
  eventDate,
  recordDate,
  newShare,
  subscriptionPrice,
  issuerCountry,
}: IRightsIssueEventDetailsProps) {
  const name = `RightsIssueEventDetails/${eventDate}`;
  const parsedEventDate = parseDateYmd(eventDate);

  return (
    <EventDetailsPopover name={name}>
      <>
        {recordDate && (
          <>
            <strong>Data-com</strong>
            <time className="font-monospace" dateTime={parseDateYmd(recordDate).toString()}>
              {parseDateYmd(recordDate).toLocaleDateString("pt-BR")}
            </time>
          </>
        )}

        <strong>Pagamento</strong>
        <time dateTime={parsedEventDate.toString()}>{parsedEventDate.toLocaleDateString("pt-BR")}</time>

        {newShare && (
          <>
            <strong>Subscrição</strong>
            <span className="font-monospace">{newShare * 100}%</span>
          </>
        )}

        {subscriptionPrice && (
          <>
            <strong>Subscrição</strong>
            <span className="font-monospace">{`${issuerCountry === "US" ? "$" : "R$"}${subscriptionPrice}`}</span>
          </>
        )}
      </>
    </EventDetailsPopover>
  );
}

interface ISpinOffEventDetailsProps {
  excludingDate: string;
  spinFrom: number;
  spinTo: number;
  numberOfShares: number;
  childCompanyName: string;
  childCompanyExchangeId: string;
  childCompanySymbol: string;
}

export function SpinOffEventDetails({
  excludingDate,
  spinFrom,
  spinTo,
  numberOfShares,
  childCompanyName,
  childCompanySymbol,
  childCompanyExchangeId,
}: ISpinOffEventDetailsProps) {
  const name = `SpinOffEventDetails/${excludingDate}`;
  const formatValue = (value?: number | null) => {
    if (!value) {
      return "-";
    }

    return trimEnd(trimEnd("0", numeral(value).format("0.000").replace(".", ",")), ",");
  };

  return (
    <EventDetailsPopover name={name}>
      <>
        <strong>Data de Conversão</strong>
        <time className="font-monospace" dateTime={parseDateYmd(excludingDate).toString()}>
          {parseDateYmd(excludingDate).toLocaleDateString("pt-BR")}
        </time>

        <strong>Proporção</strong>
        <span className="font-monospace">
          {formatValue(spinFrom)}:{formatValue(spinTo)}
        </span>

        <strong>Quantidade de Ações</strong>
        <span className="font-monospace">{formatValue(numberOfShares)}</span>

        <strong>Nova Empresa</strong>
        <span className="font-monospace">{childCompanyName}</span>

        <strong>Listagem</strong>
        <span className="font-monospace">{childCompanyExchangeId}</span>

        <strong>Ticker</strong>
        <span className="font-monospace">{childCompanySymbol}</span>
      </>
    </EventDetailsPopover>
  );
}

interface IEventDetailsPopoverProps {
  name: string;
  children: React.ReactElement;
}

function EventDetailsPopover({ name, children }: IEventDetailsPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <div className="hidden md:flex">
        <Hover key={name}>
          {({ bind, hovered }) => (
            <Popover
              className="grid w-max grid-cols-2 gap-2 rounded-xl border border-gray-200 p-2 font-aeonik text-xs normal-case text-gray-500 shadow-md"
              trapFocus={true}
              id={name}
              isOpen={hovered}
              showArrow={false}
              renderWithPortal={false}
              backgroundColor="#FFFFFF"
              containerStyle={{
                zIndex: 9999,
                borderRadius: 12,
              }}
              render={() => children}
            >
              {({ accessibility, ref }) => (
                <span
                  {...accessibility}
                  {...bind}
                  ref={ref}
                  className="inline-flex select-none items-center rounded-md border border-indigo-100 bg-indigo-50 p-1 font-aeonik text-xs font-medium uppercase text-indigo-600 hover:cursor-pointer"
                >
                  Ver Detalhes
                </span>
              )}
            </Popover>
          )}
        </Hover>
      </div>

      <div className="flex md:hidden">
        <Popover
          className="grid w-max grid-cols-2 gap-2 rounded-xl border border-gray-200 p-2 font-aeonik text-xs normal-case text-gray-500 shadow-md"
          trapFocus={true}
          id={name}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          showArrow={false}
          renderWithPortal={false}
          backgroundColor="#FFFFFF"
          containerStyle={{
            zIndex: 9999,
            borderRadius: 12,
          }}
          render={() => children}
        >
          {({ accessibility, ref }) => (
            <button
              {...accessibility}
              ref={ref}
              className="m-0 flex"
              onClick={() => {
                setIsOpen((previous) => !previous);
              }}
              type="button"
            >
              <span className="inline-flex select-none items-center rounded-md border border-indigo-100 bg-indigo-50 p-1 font-aeonik text-xs font-medium uppercase text-indigo-600 hover:cursor-pointer">
                Ver Detalhes
              </span>
            </button>
          )}
        </Popover>
      </div>
    </>
  );
}

function CheckCircleIcon(props: React.SVGProps<any>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6" {...props}>
      <path
        fillRule="evenodd"
        d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
        clipRule="evenodd"
      />
    </svg>
  );
}
